import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerModal } from '../Modal'
import PerText from '../Text'
import {
  KudosCompanyCountryVisibilityDto,
  KudosCompanyCountryVisibilityFilterInput,
  KudosCompanyCountryVisibilityInput,
} from '../../../types'
import { createOrEditKudosCompanyCountryVisibility } from '../../../graphql/kudos/useKudosMutations'
import { showNotification } from '../Notification/notification'

export interface EditKudosCompanyCountryVisibilityModalProps {
  visible: boolean
  onClose: () => void
  kudosCompanyCountryVisibility: KudosCompanyCountryVisibilityDto | null
  filters: KudosCompanyCountryVisibilityFilterInput
  resetFiltersAfterVisibilityChange: () => void
}

export const EditKudosCompanyCountryVisibilityModal: FC<EditKudosCompanyCountryVisibilityModalProps> = ({
  visible,
  onClose,
  kudosCompanyCountryVisibility,
  filters,
  resetFiltersAfterVisibilityChange,
}) => {
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleEdit = async () => {
    if (!kudosCompanyCountryVisibility) return

    setIsLoading(true)
    setErrorMessage('')

    const input: KudosCompanyCountryVisibilityInput = {
      companyId: kudosCompanyCountryVisibility.companyId,
      country: kudosCompanyCountryVisibility.country,
      kudosEnabled: !kudosCompanyCountryVisibility.kudosEnabled,
    }
    const result = await createOrEditKudosCompanyCountryVisibility(input, filters)

    if (!!result.data && !result.errors) {
      resetAndClose()
      resetFiltersAfterVisibilityChange()
      showNotification({
        type: 'success',
        message: t('kudos.notifications.editCompanyCountryVisibility.successTitle'),
        description: t('kudos.notifications.editCompanyCountryVisibility.successDescription'),
      })
    } else {
      const errorMessage =
        result.errors?.[0]?.extensions?.validationErrors?.[0]?.message || t('kudos.settings.editCompanyCountryVisibilityModal.error')
      setErrorMessage(errorMessage)
      setIsLoading(false)
    }
  }

  const resetAndClose = () => {
    setErrorMessage('')
    setIsLoading(false)
    onClose()
  }

  if (!kudosCompanyCountryVisibility) return null

  return (
    <PerModal
      title={{ heading: t('kudos.settings.editCompanyCountryVisibilityModal.title') }}
      size="medium"
      centered
      visible={visible}
      onCancel={resetAndClose}
      footer={{
        cancelBtn: {
          onClick: resetAndClose,
        },
        confirmBtn: {
          onClick: handleEdit,
          disabled: isLoading || !!errorMessage,
          isProcessing: { active: isLoading },
        },
      }}
    >
      {kudosCompanyCountryVisibility.kudosEnabled ? (
        <>
          <PerText fontSize="m">{t(`kudos.settings.editCompanyCountryVisibilityModal.disable1`)}</PerText>
          <PerText fontSize="m">{t(`kudos.settings.editCompanyCountryVisibilityModal.disable2`)}</PerText>
          <PerText fontSize="m" mt="xxs" fontWeight="bold">
            {t(`kudos.settings.editCompanyCountryVisibilityModal.disable3`)}
          </PerText>
        </>
      ) : (
        <>
          <PerText fontSize="m">{t(`kudos.settings.editCompanyCountryVisibilityModal.enable1`)}</PerText>
          <PerText fontSize="m">{t(`kudos.settings.editCompanyCountryVisibilityModal.enable2`)}</PerText>
          <PerText fontSize="m" mt="xxs" fontWeight="bold">
            {t(`kudos.settings.editCompanyCountryVisibilityModal.enable3`)}
          </PerText>
        </>
      )}

      {errorMessage && (
        <PerText color="error" fontSize="m" mt="xs">
          {errorMessage}
        </PerText>
      )}
    </PerModal>
  )
}

export default EditKudosCompanyCountryVisibilityModal
